<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue'
import QuizProgress from '@/components/Quiz/QuizProgress.vue'
import NotFound from '@/components/Quiz/Steps/NotFound.vue'
import QuizAlert from '@/components/Quiz/QuizAlert.vue'
import {useQuizStore} from '@/stores/quizStore';
import {showClaim} from "@/services/quizService.ts";
import BodyContent from "@/components/Ui/Carousel/BodyContent.vue";
import {captureException, captureMessage} from "@sentry/browser";
import LayoutFooter from "@/components/LayoutFooter.vue";

import VehicleIdentification from "@/components/Quiz/Steps/VehicleIdentification.vue"
import EmailAndAgreement from "@/components/Quiz/Steps/EmailAndAgreement.vue"
import VehiclesSelection from "@/components/Quiz/Steps/VehiclesSelection.vue"
import VehicleAcquisitionDates from "@/components/Quiz/Steps/VehicleAcquisitionDates.vue"
import AddressesSelection from "@/components/Quiz/Steps/AddressesSelection.vue"
import ContactForm from "@/components/Quiz/Steps/ContactForm.vue"
import FeeCap from "@/components/Quiz/Steps/FeeCap.vue"
import SignaturePad from "@/components/Quiz/Steps/SignaturePad.vue"
import CreditReportAuthentication from "@/components/Quiz/Steps/CreditReportAuthentication.vue"
import CreditReport from "@/components/Quiz/Steps/CreditReport.vue"

const {
  step,
  alert,
  closeAlert,
  steps,
  vin,
  vrm,
  token,
  stepRouteParam,
  setIsFetching,
  continueQuiz,
  resetQuiz,
  restoreData,
  showAlert
} = useQuizStore();

const isReady = ref(false)

const components = [
  VehicleIdentification,
  EmailAndAgreement,
  VehiclesSelection,
  VehicleAcquisitionDates,
  AddressesSelection,
  ContactForm,
  FeeCap,
  SignaturePad,
  CreditReportAuthentication,
  CreditReport,
]

const stepExists = computed(() => {
  return step.value <= steps.value && step.value >= 0
})

onMounted(() => {
  if (token.value) {
    setIsFetching(true)
    showClaim(token.value)
      .then((res) => {
        restoreData(res)
        continueQuiz()
      })
      .catch(async (err) => {
        const response = await err.response?.json()
        if (response?.error) {
          showAlert(response.error)
        } else {
          captureMessage("Error on showClaim");
          captureException(err);
        }
        await resetQuiz()
      })
      .finally(() => {
        setIsFetching(false)
        isReady.value = true
      })
    return
  }

  if (vrm.value || vin.value && stepRouteParam === 2) {
    isReady.value = true
    return
  }

  if (stepRouteParam > 1 && !token.value) {
    resetQuiz()
    isReady.value = true
    return
  }

  isReady.value = true
})
</script>

<template>
  <div
    class="min-h-screen bg-[#0F8F52] flex flex-col items-center justify-between pt-6 pb-4 w-full">

    <div class="w-full max-w-[500px] relative pt-10">
      <h1
        class="flex items-center justify-between text-white font-bold text-xl absolute w-[136px] top-0 left-1/2 -translate-x-1/2">
        <img src="@/assets/images/logo.webp" alt="Claims For Cars" class="">
      </h1>

      <div v-if="isReady" class="bg-white rounded-xl pt-10 pb-6 px-4 lg:px-6">
        <QuizProgress v-if="stepExists"/>

        <BodyContent v-if="stepExists" :totalItems="steps" :currentIndex="step">
          <template v-for="index in steps" #[`item-${index}`] :key="`step-${index}`">
            <component v-if="step === index - 1" :is="components[index - 1]"/>
            <div style="min-height: 400px" v-else/>
          </template>
        </BodyContent>

        <NotFound v-else/>

        <QuizAlert :type="alert.type" :message="alert.message" :show="alert.show"
                   @close="closeAlert"
                   class="mt-4"/>
      </div>
    </div>

    <LayoutFooter class="w-full"/>
  </div>
</template>
